import { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Radio, Globe, MapPin, Mail, Link2, ArrowLeft, Upload,
  CheckCircle, AlertCircle, Loader2, X, Shield, Zap, Crown
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { BACKEND_URL } from "../context/AuthContext";

const API = BACKEND_URL;

const GENRE_OPTIONS = [
  "Hip Hop", "R&B", "Pop", "Rock", "Jazz", "Blues", "Country",
  "Electronic", "House", "Reggae", "Latin", "Gospel", "Classical",
  "Afrobeats", "Dancehall", "Soul", "Neo Soul", "Funk", "Metal", "Indie",
  "Talk Radio", "Sports", "News", "Variety", "Other"
];

const trackEvent = (event, meta) => {
  fetch(`${API}/api/directory/track-event?event=${encodeURIComponent(event)}${meta ? `&meta=${encodeURIComponent(meta)}` : ""}`, { method: "POST" }).catch(() => {});
};

export default function DirectorySubmitPage() {
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    station_name: "",
    genre: [],
    stream_url: "",
    description: "",
    logo_url: "",
    location: "",
    language: "English",
    website: "",
    social_links: { facebook: "", twitter: "", instagram: "" },
    contact_email: "",
    why_join: "",
    priority: false,
  });

  // Handle PayPal return
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const listingId = searchParams.get("listing_id");
    const paymentId = searchParams.get("paymentId");
    const payerId = searchParams.get("PayerID");

    if (paymentStatus === "success" && paymentId && payerId) {
      setPaymentProcessing(true);
      fetch(`${API}/api/directory/priority-capture?payment_id=${paymentId}&payer_id=${payerId}`, { method: "POST" })
        .then(r => r.json())
        .then(() => {
          setSuccess(true);
          trackEvent("priority_payment_completed", listingId);
        })
        .catch(() => setError("Payment capture failed. Please contact support."))
        .finally(() => setPaymentProcessing(false));
    } else if (paymentStatus === "cancel") {
      setError("Payment was cancelled. Your application was still submitted for free review.");
      setSuccess(true);
    }
  }, [searchParams]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/directory/upload-logo`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      set("logo_url", d.url);
    } catch (err) {
      setError(err.message);
      setLogoPreview(null);
    }
    setUploading(false);
  };

  const removeLogo = () => {
    set("logo_url", "");
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.station_name.trim()) return setError("Station name is required");
    if (!form.genre.length) return setError("Please select at least one genre");
    if (!form.stream_url.trim()) return setError("Stream URL is required");
    if (!form.contact_email.trim()) return setError("Contact email is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) return setError("Invalid email address");

    setSubmitting(true);
    trackEvent("station_apply", form.station_name);
    if (form.priority) trackEvent("priority_selected", form.station_name);

    try {
      const cleanSocial = {};
      Object.entries(form.social_links).forEach(([k, v]) => {
        if (v.trim()) cleanSocial[k] = v.trim();
      });
      const r = await fetch(`${API}/api/directory/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, social_links: cleanSocial }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Submission failed");

      // If priority selected, redirect to PayPal
      if (form.priority && d.id) {
        trackEvent("priority_payment_started", d.id);
        const payRes = await fetch(`${API}/api/directory/priority-pay?listing_id=${d.id}`, { method: "POST" });
        const payData = await payRes.json();
        if (payRes.ok && payData.approval_url) {
          window.location.href = payData.approval_url;
          return;
        }
        // PayPal failed — still show success for the free submission
        setSuccess(true);
        setError("Application submitted, but priority payment could not be processed. You're still in the free review queue.");
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setSuccess(false);
    setLogoPreview(null);
    setError("");
    setForm({ station_name: "", genre: [], stream_url: "", description: "", logo_url: "", location: "", language: "English", website: "", social_links: { facebook: "", twitter: "", instagram: "" }, contact_email: "", why_join: "", priority: false });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (paymentProcessing) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-red-500 animate-spin mx-auto mb-4" />
          <p className="text-white/60 text-sm">Processing your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <SEO title="Apply to Join The NU Vibe Network" description="Apply to have your radio station listed on The NU Vibe Network. Limited stations accepted." />
      <Header />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <Link to="/discover" className="inline-flex items-center gap-1.5 text-white/40 hover:text-red-400 text-sm mb-6 transition" data-testid="back-to-discover">
          <ArrowLeft className="w-4 h-4" /> Back to Discover
        </Link>

        {success ? (
          <div className="text-center py-16" data-testid="submit-success">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {searchParams.get("payment") === "success" ? "Priority Application Received!" : "Application Received!"}
            </h1>
            <p className="text-white/50 mb-6 max-w-md mx-auto">
              {searchParams.get("payment") === "success"
                ? "Your priority application has been received and paid. You'll be reviewed first and featured upon approval."
                : "Thanks for applying to The NU Vibe Network. Our team will review your station and reach out if approved. Only select stations are accepted."}
            </p>
            {error && <p className="text-amber-400/80 text-xs mb-4">{error}</p>}
            <div className="flex items-center justify-center gap-3">
              <Link to="/discover" className="bg-red-600 hover:bg-red-500 text-white font-semibold px-5 py-2.5 rounded-lg transition text-sm" data-testid="back-discover-btn">Back to Discover</Link>
              <button onClick={resetForm} className="bg-white/10 hover:bg-white/15 text-white px-5 py-2.5 rounded-lg transition text-sm">Submit Another</button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-600/20 flex items-center justify-center">
                  <Radio className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white" data-testid="apply-heading">Apply to Join The NU Vibe Network</h1>
                  <p className="text-white/40 text-sm">Get your station featured alongside the best in live radio</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-red-600/8 border border-red-500/15 rounded-lg px-4 py-2.5" data-testid="scarcity-banner">
                <Shield className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-red-400/80 text-xs">Limited stations accepted into the network. We review every application to maintain quality.</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6 text-red-400 text-sm" data-testid="submit-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="directory-submit-form">
              {/* Station Name */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Station Name *</label>
                <div className="relative">
                  <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input type="text" value={form.station_name} onChange={(e) => set("station_name", e.target.value)}
                    placeholder="e.g. Hot 97 FM"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 text-sm"
                    data-testid="input-station-name" />
                </div>
              </div>

              {/* Genre */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Genre(s) *</label>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="genre-checkboxes">
                  {GENRE_OPTIONS.map((g) => (
                    <label key={g} className="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" checked={form.genre.includes(g)}
                        onChange={(e) => {
                          if (e.target.checked) set("genre", [...form.genre, g]);
                          else set("genre", form.genre.filter(x => x !== g));
                        }}
                        className="w-3.5 h-3.5 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500/30 accent-red-500"
                        data-testid={`genre-check-${g.toLowerCase().replace(/\s/g, '-')}`} />
                      <span className="text-white/60 text-sm group-hover:text-white/80 transition">{g}</span>
                    </label>
                  ))}
                </div>
                {form.genre.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.genre.map(g => (
                      <span key={g} className="bg-red-500/15 text-red-400 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                        {g}
                        <button type="button" onClick={() => set("genre", form.genre.filter(x => x !== g))} className="hover:text-white transition"><X className="w-2.5 h-2.5" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Stream URL */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Stream URL *</label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input type="url" value={form.stream_url} onChange={(e) => set("stream_url", e.target.value)}
                    placeholder="https://stream.example.com:8000/live"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 text-sm"
                    data-testid="input-stream-url" />
                </div>
                <p className="text-white/15 text-xs mt-1">Shoutcast, Icecast, or direct stream URL</p>
              </div>

              {/* Location */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">City / Location *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input type="text" value={form.location} onChange={(e) => set("location", e.target.value)}
                    placeholder="e.g. Atlanta, GA"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 text-sm"
                    data-testid="input-location" />
                </div>
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Contact Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)}
                    placeholder="you@station.com"
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 text-sm"
                    data-testid="input-contact-email" />
                </div>
              </div>

              {/* Why Join */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Why should we add your station? <span className="text-white/20">(optional)</span></label>
                <textarea value={form.why_join} onChange={(e) => set("why_join", e.target.value)}
                  placeholder="Tell us what makes your station stand out..."
                  rows={3}
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 px-4 text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 text-sm resize-none"
                  data-testid="input-why-join" />
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Station Logo <span className="text-white/20">(optional)</span></label>
                {logoPreview || form.logo_url ? (
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/5 border border-white/[0.06] flex-shrink-0">
                      <img src={logoPreview || form.logo_url} alt="Logo preview" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-2">Logo uploaded</p>
                      <button type="button" onClick={removeLogo} className="flex items-center gap-1.5 text-red-400 hover:text-red-300 text-xs transition" data-testid="remove-logo">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="w-full border-2 border-dashed border-white/[0.06] hover:border-red-500/30 rounded-xl py-6 flex flex-col items-center gap-2 transition text-white/30 hover:text-red-400 disabled:opacity-50"
                    data-testid="upload-logo-btn">
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                    <span className="text-xs">{uploading ? "Uploading..." : "Click to upload station logo"}</span>
                    <span className="text-[10px] text-white/15">PNG, JPG, WebP — max 5MB</span>
                  </button>
                )}
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
              </div>

              {/* Divider */}
              <div className="border-t border-white/[0.04] pt-6">
                {/* Priority Review Option */}
                <div className={`relative rounded-xl border p-5 transition-all cursor-pointer ${
                  form.priority
                    ? "bg-amber-500/10 border-amber-500/30"
                    : "bg-white/[0.02] border-white/[0.06] hover:border-amber-500/20"
                }`}
                  onClick={() => set("priority", !form.priority)}
                  data-testid="priority-option">
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition ${
                      form.priority ? "bg-amber-500 border-amber-500" : "border-white/20"
                    }`}>
                      {form.priority && <CheckCircle className="w-3 h-3 text-black" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Zap className="w-4 h-4 text-amber-400" />
                        <span className="text-white font-bold text-sm">Priority Review + Featured Placement</span>
                        <span className="bg-amber-500/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full">$10</span>
                      </div>
                      <p className="text-white/35 text-xs leading-relaxed">
                        Get reviewed first and automatically featured on the Discover page upon approval. Skip the wait.
                      </p>
                    </div>
                  </div>
                  {form.priority && (
                    <div className="mt-3 ml-9 flex items-center gap-1.5 text-amber-400/60 text-[10px]">
                      <Crown className="w-3 h-3" /> You'll be redirected to PayPal after submitting
                    </div>
                  )}
                </div>
              </div>

              {/* Tier comparison */}
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className={`rounded-lg border p-3 transition ${!form.priority ? "border-white/10 bg-white/[0.02]" : "border-white/[0.04] bg-transparent"}`}>
                  <p className="text-white/60 text-xs font-semibold mb-1">Free Application</p>
                  <p className="text-white/25 text-[10px]">Standard review queue</p>
                  <p className="text-white/25 text-[10px]">Listed upon approval</p>
                </div>
                <div className={`rounded-lg border p-3 transition ${form.priority ? "border-amber-500/30 bg-amber-500/5" : "border-white/[0.04] bg-transparent"}`}>
                  <p className="text-amber-400 text-xs font-semibold mb-1">Priority — $10</p>
                  <p className="text-white/25 text-[10px]">Reviewed first</p>
                  <p className="text-white/25 text-[10px]">Featured placement</p>
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={submitting || uploading}
                className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2 text-sm"
                data-testid="submit-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <Radio className="w-4 h-4" />
                    {form.priority ? "Submit & Pay $10 for Priority" : "Apply to Join The NU Vibe Network"}
                  </>
                )}
              </button>
              <p className="text-white/15 text-xs text-center">
                {form.priority ? "You'll be redirected to PayPal to complete your priority payment." : "All applications are manually reviewed. Limited spots available."}
              </p>
            </form>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
